import styled from 'styled-components';
import {
  ColorSwatch,
  LegendComponentType,
  LegendReferenceType,
} from '@ardoq/graph';
import {
  LegendItemModel,
  ViewLegendRowModel,
  ViewLegendSection,
} from './types';
import { ReferenceTypePreview } from '@ardoq/renderers';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { getIcon } from '@ardoq/icons';
import { colors, s8 } from '@ardoq/design-tokens';
import { isTruthy } from '@ardoq/common-helpers';
import { COLLAPSED_REFERENCE_BOX_SHADOW } from '@ardoq/dependency-map';
import { type I18nLocale, t } from '@ardoq/i18n';

try {
  // Fails when typechecking the discover app, discover doesn't use webpack. It's not considered an error in ardoq-front
  // so we can't use @ts-expect-error
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore todo
  if (module.hot) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore todo
    module.hot.accept([
      '@ardoq/graph',
      '@ardoq/renderers',
      '@ardoq/icons',
      '@ardoq/design-tokens',
      '@ardoq/common-helpers',
      '@ardoq/dependency-map',
    ]);
  }
} catch (e) {
  /* empty */
}

const ITEM_HEIGHT = 18;

const GraphViewLegendNodeSymbol = styled.div`
  width: 44px;
  height: ${ITEM_HEIGHT}px;
  border: 1px solid ${colors.grey900};
`;
const GraphViewLegendIconAndSwatchContainer = styled.div`
  display: flex;
  height: ${ITEM_HEIGHT}px;
`;
const GraphViewLegendIconContainer = styled.div`
  width: ${ITEM_HEIGHT}px;
  font-size: ${ITEM_HEIGHT}px;

  &:not(:last-child) {
    margin-right: ${s8};
  }

  line-height: 15px;
  text-align: center;
`;
const GraphViewLegendColorSwatch = styled(ColorSwatch)`
  border: solid 1px;
`;

const collapsedNodeDescription = (
  i18nLocale: I18nLocale,
  isUserDefinedGrouping: boolean
) =>
  isUserDefinedGrouping
    ? t(
        i18nLocale,
        'viewLegend:legend.collapsedGroupDepth',
        'Collapsed group depth'
      )
    : t(
        i18nLocale,
        'viewLegend:legend.collapsedComponentHierarchy',
        'Collapsed component hierarchy'
      );

type ComponentsLegendRowArgs = {
  componentTypes: LegendComponentType[];
  hasCollapsedNodes: boolean;
  hasReferenceParents: boolean;
  hasNonComponentNodes: boolean;
  isUserDefinedGrouping: boolean;
  showComponentSwatches: boolean;
  showComponentShapes: boolean;
  isComponentTypesHeadingVisible?: boolean;
  selectableItems?: boolean;
  setSelectedComponentItems?: (
    items: LegendItemModel[],
    componentTypes: LegendComponentType[]
  ) => void;
  onComponentMouseOver?: (componentType: LegendComponentType | null) => void;
  i18nLocale: I18nLocale;
};
export const componentsLegendRow = ({
  componentTypes,
  hasCollapsedNodes,
  hasReferenceParents,
  hasNonComponentNodes,
  isUserDefinedGrouping,
  showComponentSwatches,
  showComponentShapes,
  selectableItems,
  onComponentMouseOver,
  setSelectedComponentItems,
  i18nLocale,
  isComponentTypesHeadingVisible = true,
}: ComponentsLegendRowArgs): ViewLegendRowModel | null => {
  if (!componentTypes.length) {
    return null;
  }

  return {
    id: 'Components',
    title: t(i18nLocale, 'viewLegend:components', 'Components'),
    sections: [
      {
        heading: isComponentTypesHeadingVisible
          ? t(i18nLocale, 'viewLegend:legend.componentType', 'Component type')
          : null,
        items: componentTypes.map(componentType => {
          const {
            name,
            icon,
            image,
            borderColor,
            backgroundColor,
            ids,
            isSelected,
          } = componentType;
          return {
            id: ids.join(','),
            isSelected,
            label: name,
            getShape: () => (
              <GraphViewLegendIconAndSwatchContainer>
                <GraphViewLegendIconContainer>
                  <ComponentRepresentation
                    isImage={Boolean(image)}
                    value={image ?? null}
                    icon={getIcon(icon)}
                  />
                </GraphViewLegendIconContainer>
                {showComponentSwatches && (
                  <GraphViewLegendColorSwatch
                    style={{
                      borderColor,
                      backgroundColor,
                      visibility:
                        !borderColor && !backgroundColor ? 'hidden' : undefined,
                    }}
                  />
                )}
              </GraphViewLegendIconAndSwatchContainer>
            ),
            onMouseOver:
              selectableItems && onComponentMouseOver
                ? () => onComponentMouseOver(componentType)
                : undefined,
            onMouseOut:
              selectableItems && onComponentMouseOver
                ? () => onComponentMouseOver(null)
                : undefined,
          };
        }),
        selectableItems,
        setSelectedItems: setSelectedComponentItems
          ? (items: LegendItemModel[]) => {
              setSelectedComponentItems(items, componentTypes);
            }
          : undefined,
      },
      showComponentShapes && {
        heading: hasNonComponentNodes
          ? t(
              i18nLocale,
              'viewLegend:legend.componentAndGroup',
              'Component and group'
            )
          : t(i18nLocale, 'viewLegend:legend.component', 'Component'),
        items: [
          {
            label: t(i18nLocale, 'viewLegend:legend.component', 'Component'),
            getShape: () => <GraphViewLegendNodeSymbol />,
          },
          hasNonComponentNodes && {
            label: t(i18nLocale, 'viewLegend:legend.group', 'Group'),
            getShape: () => (
              <GraphViewLegendNodeSymbol
                style={{ backgroundColor: colors.grey200 }}
              />
            ),
          },
          {
            label: t(
              i18nLocale,
              'viewLegend:legend.referencedComponent',
              'Referenced component'
            ),
            getShape: () => (
              <GraphViewLegendNodeSymbol
                style={{
                  borderRadius: ITEM_HEIGHT / 2,
                }}
              />
            ),
          },
        ].filter(isTruthy),
      },
      hasReferenceParents && {
        heading: t(
          i18nLocale,
          'viewLegend:legend.includeReferenceParents',
          'Include reference parents'
        ),
        items: [
          {
            label: t(
              i18nLocale,
              'viewLegend:legend.parentReferencedComponent',
              'Parent of referenced component'
            ),
            getShape: () => (
              <GraphViewLegendNodeSymbol
                style={{
                  borderWidth: 2,
                  borderStyle: 'dotted',
                  borderRadius: ITEM_HEIGHT / 2,
                  opacity: 0.5,
                }}
              />
            ),
          },
        ],
      },
      hasCollapsedNodes && {
        heading: collapsedNodeDescription(i18nLocale, isUserDefinedGrouping),
        items: [
          {
            label: t(
              i18nLocale,
              'viewLegend:legend.rolledupReferencedComponent',
              'Rolled-up referenced component'
            ),
            getShape: () => (
              <GraphViewLegendNodeSymbol
                style={{
                  boxShadow: COLLAPSED_REFERENCE_BOX_SHADOW,
                  borderRadius: ITEM_HEIGHT / 2,
                }}
              />
            ),
          },
          {
            label: collapsedNodeDescription(i18nLocale, isUserDefinedGrouping),
            getShape: () => (
              <GraphViewLegendNodeSymbol
                style={{
                  borderWidth: 3,
                  borderStyle: 'double',
                }}
              />
            ),
          },
        ],
      },
    ].filter(isTruthy),
  };
};

type ReferencesLegendRowArgs = {
  referenceTypes: LegendReferenceType[];
  showReferencesAsLines?: boolean;
  selectableItems?: boolean;
  setSelectedReferenceItems?: (
    items: LegendItemModel[],
    referenceTypes: LegendReferenceType[]
  ) => void;
  onReferenceMouseOver?: (referenceType: LegendReferenceType | null) => void;
  i18nLocale: I18nLocale;
};

export const referencesLegendRow = ({
  referenceTypes,
  showReferencesAsLines,
  selectableItems,
  onReferenceMouseOver,
  i18nLocale,
  setSelectedReferenceItems,
}: ReferencesLegendRowArgs): ViewLegendRowModel | null => {
  if (!referenceTypes.length) {
    return null;
  }

  return {
    id: 'References',
    title: t(i18nLocale, 'viewLegend:references', 'References'),
    sections: [
      {
        heading: null,
        items: referenceTypes.map(referenceType => ({
          id: referenceType.globalTypeIds.join(','),
          isSelected: referenceType.isSelected,
          label: referenceType.name,
          getShape: () =>
            showReferencesAsLines ? (
              <ReferenceTypePreview height={20} width={42} {...referenceType} />
            ) : (
              <GraphViewLegendColorSwatch className={referenceType.className} />
            ),
          onMouseOver:
            selectableItems && onReferenceMouseOver
              ? () => onReferenceMouseOver(referenceType)
              : undefined,
          onMouseOut:
            selectableItems && onReferenceMouseOver
              ? () => onReferenceMouseOver(null)
              : undefined,
        })),
        selectableItems,
        setSelectedItems: setSelectedReferenceItems
          ? items => {
              setSelectedReferenceItems(items, referenceTypes);
            }
          : undefined,
      },
    ],
  };
};

type FormattingLegendRowArgs = {
  formatting: ViewLegendSection[] | null;
  selectableItems?: boolean;
  selectedIds?: Set<string>;
  getSelectedItemsSetter?: (
    formatting: ViewLegendSection[],
    selectedIds?: Set<string>
  ) => (section: ViewLegendSection) => (items: LegendItemModel[]) => void;
  selectableFormattingSection?: (
    section: ViewLegendSection,
    selectedIds: Set<string> | null,
    setSelectedItems: (items: LegendItemModel[]) => void
  ) => ViewLegendSection;
  i18nLocale: I18nLocale;
};

export const formattingLegendRow = ({
  formatting,
  selectableItems,
  selectedIds,
  getSelectedItemsSetter,
  i18nLocale,
  selectableFormattingSection,
}: FormattingLegendRowArgs): ViewLegendRowModel | null => {
  if (!formatting) {
    return null;
  }

  const setSelectedItems =
    getSelectedItemsSetter && getSelectedItemsSetter(formatting, selectedIds);

  return {
    id: 'Conditional Formatting',
    title: t(
      i18nLocale,
      'viewLegend:conditionalFormatting',
      'Conditional Formatting'
    ),
    sections:
      selectableItems && setSelectedItems && selectableFormattingSection
        ? formatting.map(section =>
            selectableFormattingSection(
              section,
              selectedIds ?? null,
              setSelectedItems(section)
            )
          )
        : formatting,
  };
};
